import { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { toast, ToastOptions } from 'react-toastify';
import { useEffectOnce } from 'react-use';

import {
  Button,
  CalloutStatementsError,
  CalloutStatementsNone,
  CalloutStatementsNoResult,
  Col,
  CountLabel,
  LoadingIndicator,
  PageContainer,
  Row,
  StatementList,
  useModal,
} from '@xbto/universal-components';
import { theme } from '@xbto/design-system';
import { TOAST_OPTIONS } from 'common';
import { DataStore } from '~/store';
import { StatementsFiltersForm } from './statements-filters-form';

export function ListOfStatements() {
  /**
   * Store
   */
  const error = DataStore.useStoreState(s => s.statements.error);
  const isLoading = DataStore.useStoreState(s => s.statements.busy);
  const isLastPage = DataStore.useStoreState(s => s.statements.isLastPage);
  const appliedFiltersCount = DataStore.useStoreState(
    s => s.statements.appliedFiltersCount
  );
  const hasNoStatementsYet = DataStore.useStoreState(
    s => s.statements.hasNoStatementsYet
  );
  const statements = DataStore.useStoreState(s => s.statements.listFull);

  const { clearFilters, getStatementPdfUrl, paginateList, refreshList } =
    DataStore.useStoreActions(a => ({
      clearFilters: a.statements.clearFilters,
      getStatementPdfUrl: a.statements.getStatementPdfUrl,
      paginateList: a.statements.paginateListFull,
      refreshList: a.statements.refreshListFull,
    }));

  /**
   * State
   */
  const [isGenerating, setGenerating] = useState(false);
  const [isPaginating, setPaginating] = useState(false);

  /**
   * Vars
   */
  const { open: openModal, close: closeModal, Modal } = useModal();

  const resetList = useCallback(() => {
    clearFilters();
    refreshList();
  }, [clearFilters, refreshList]);

  /**
   * Handlers
   */
  const handlePressItem = useCallback(
    async (statementId: string, accountId: string) => {
      try {
        setGenerating(true);

        const pdfUrl = await getStatementPdfUrl({ accountId, statementId });

        window.open(pdfUrl, '_blank');
      } catch (err) {
        toast.warn(
          (err as unknown as Error).message,
          TOAST_OPTIONS as ToastOptions
        );
      } finally {
        setGenerating(false);
      }
    },
    [getStatementPdfUrl]
  );

  const handlePressFilters = useCallback(() => {
    openModal({
      Body: <StatementsFiltersForm onClose={closeModal} />,
    });
  }, [closeModal, openModal]);

  const handlePaginate = useCallback(async () => {
    try {
      setPaginating(true);

      await paginateList();
    } finally {
      setPaginating(false);
    }
  }, [paginateList]);

  /**
   * Effects
   */
  useEffectOnce(() => {
    refreshList();

    return clearFilters;
  });

  /**
   * DOM
   */
  const header = (
    <Row.LR style={$.actionBar}>
      <Button
        disabled={hasNoStatementsYet || !!error}
        label="Filters"
        labelAddon={
          appliedFiltersCount > 0 ? (
            <CountLabel count={appliedFiltersCount} mx="2" />
          ) : undefined
        }
        variant="tertiary"
        size="sm"
        icon="FilterList"
        onPress={handlePressFilters}
      />
    </Row.LR>
  );

  let content;

  if (error) {
    content = (
      <>
        {header}
        <Col mt="6" style={$.expand}>
          <CalloutStatementsError onRetry={refreshList} />
        </Col>
      </>
    );
  } else if (isLoading && !isPaginating && !isGenerating) {
    content = (
      <>
        {header}
        <Row.C p="8">
          <LoadingIndicator />
        </Row.C>
      </>
    );
  } else if (hasNoStatementsYet) {
    content = (
      <>
        {header}
        <Col mt="6" style={$.expand}>
          <CalloutStatementsNone />
        </Col>
      </>
    );
  } else if (!statements?.length) {
    content = (
      <>
        {header}
        <Col mt="6" style={$.expand}>
          <CalloutStatementsNoResult onClearFilters={resetList} />
        </Col>
      </>
    );
  } else {
    content = (
      <StatementList
        header={header}
        data={statements ?? []}
        onPressItem={handlePressItem}
        onPaginate={handlePaginate}
        paginable={!isLastPage}
        paginating={isPaginating}
      />
    );
  }

  return (
    <>
      <PageContainer fullHeight>{content}</PageContainer>
      <Modal />
    </>
  );
}

const $ = StyleSheet.create({
  actionBar: {
    marginTop: theme.spacing['6'],
    marginBottom: -theme.spacing['2'],
  },
  expand: {
    flex: 1,
  },
});
